import { useModal } from "@/features/modal/ModalStackManager";
import { Calendar, CalendarDate } from "@/features/ui/calendar";
import { Machine, MachineSearchBox } from "@/features/ui/PMS/machineSearchBox";
import { Button, Flex, Text } from "@mantine/core";
import { IconCalendar, IconReportSearch } from "@tabler/icons-react";
import dayjs from "dayjs";
import { useState } from "react";
import DataGrid, { Column } from "react-data-grid";

export const AnalysisErrorPage = () => {
  const { openModal, closeModal } = useModal();
  const [selectedMachine, setSelectedMachine] = useState<Machine | null>(null);
  const [dates, setDates] = useState<CalendarDate>([null, null]);

  const handleSelectMachine = (machine: Machine | null) => {
    if (machine) {
      setSelectedMachine(machine);
      closeModal("");
    }
  };

  const Header = {
    error: ["기계 제조사", "기계 이름", "제조 연월일", "제조 번호"],
    errorHistory: [
      "기계 이름",
      "에러 시작 일시",
      "에러 종료 일시",
      "에러 번호",
      "명칭",
      "키캠 상태",
      "로드톤",
    ],
    errorView: [
      "에러 번호",
      "명칭",
      "총 에러 발생 시간",
      "평균 에러 해제 시간",
      "발생 횟수",
      "(에러 발생 횟수)",
    ],
  };

  const BodyData = {
    error: [
      {
        "기계 제조사": "제조사1",
        "기계 이름": "이름1",
        "제조 연월일": "2022-01-01",
        "제조 번호": "1234",
      },
    ],
    errorHistory: [
      {
        "기계 이름": "기계1",
        "에러 시작 일시": "2022-01-01",
        "에러 종료 일시": "2022-01-02",
        "에러 번호": "1",
        명칭: "에러1",
        "키캠 상태": "상태1",
        로드톤: "1 ton",
      },
    ],
    errorView: [
      {
        "에러 번호": "1",
        명칭: "에러1",
        "총 에러 발생 시간": "1 hour",
        "평균 에러 해제 시간": "1 hour",
        "발생 횟수": "1",
        "(에러 발생 횟수)": "1",
      },
    ],
  };

  const machineColumns: readonly Column<any>[] = [
    {
      key: "make",
      name: "기계 제조사",
      sortable: true,
      resizable: true,
    },
    {
      key: "name",
      name: "기계 이름",
      sortable: true,
      resizable: true,
    },
    {
      key: "createdAt",
      name: "제조 연월일",
      sortable: true,
      resizable: true,
    },
    {
      key: "code",
      name: "제조 번호",
      sortable: true,
      resizable: true,
    },
  ];

  const errorHistoryColumns: readonly Column<any>[] = [
    {
      key: "machineName",
      name: "기계 이름",
      sortable: true,
      resizable: true,
    },
    {
      key: "errorStart",
      name: "에러 시작 일시",
      sortable: true,
      resizable: true,
    },
    {
      key: "errorEnd",
      name: "에러 종료 일시",
      sortable: true,
      resizable: true,
    },
    {
      key: "errorNumber",
      name: "에러 번호",
      sortable: true,
      resizable: true,
    },
    {
      key: "name",
      name: "명칭",
      sortable: true,
      resizable: true,
    },
    {
      key: "keycamStatus",
      name: "키캠 상태",
      sortable: true,
      resizable: true,
    },
    {
      key: "loadTon",
      name: "로드톤",
      sortable: true,
      resizable: true,
    },
  ];

  const errorViewColumns: readonly Column<any>[] = [
    {
      key: "errorNumber",
      name: "에러 번호",
      sortable: true,
      resizable: true,
    },
    {
      key: "name",
      name: "명칭",
      sortable: true,
      resizable: true,
    },
    {
      key: "totalErrorTime",
      name: "총 에러 발생 시간",
      sortable: true,
      resizable: true,
    },
    {
      key: "averageErrorReleaseTime",
      name: "평균 에러 해제 시간",
      sortable: true,
      resizable: true,
    },
    {
      key: "occurrenceCount",
      name: "발생 횟수",
      sortable: true,
      resizable: true,
    },
    {
      key: "errorOccurrenceCount",
      name: "(에러 발생 횟수)",
      sortable: true,
      resizable: true,
    },
  ];

  return (
    <Flex gap="md" direction="column" p="md">
      <Flex justify="space-between">
        <Text size="xl">에러</Text>
        <Flex gap="md">
          <Button
            leftIcon={<IconCalendar />}
            color="lime"
            onClick={() => {
              openModal(<Calendar dates={[null, null]} type="default" />, null, "날짜 선택").then(
                (selectedDates) => {
                  if (selectedDates) {
                    setDates(selectedDates);
                  }
                },
              );
            }}
          >
            {`${dates[0] !== null ? dayjs(dates[0]).format("YYYY-MM-DD") : ""} 날짜선택`}
          </Button>
          <Button
            onClick={() => {
              openModal(<MachineSearchBox onSelect={handleSelectMachine} />, null, "기계 검색");
            }}
            leftIcon={<IconReportSearch />}
          >
            설비선택
          </Button>
        </Flex>
      </Flex>
      <DataGrid
        columns={machineColumns ?? []}
        rows={selectedMachine ? [selectedMachine] : []}
        rowHeight={40}
        rowKeyGetter={(row) => row.id}
        style={{ height: "120px", width: "100%" }}
      />
      <Flex gap="md" direction="column">
        <Text>에러 이력</Text>
        <DataGrid
          columns={errorHistoryColumns ?? []}
          rows={machineErrorHistoryMockupData[selectedMachine?.id ?? 0] ?? []}
          rowHeight={40}
        />
        <Text>에러별 보기</Text>
        <DataGrid
          columns={errorViewColumns ?? []}
          rows={machineErrorViewMockupData[selectedMachine?.id ?? 0] ?? []}
          rowHeight={40}
        />
      </Flex>
    </Flex>
  );
};

const machineErrorHistoryMockupData: Record<
  string,
  Array<{
    machineName: string;
    errorStart: string;
    errorEnd: string;
    errorNumber: string;
    name: string;
    keycamStatus: string;
    loadTon: string;
  }>
> = {
  "1": [
    {
      machineName: "Machine 1",
      errorStart: "2024-06-01 05:40",
      errorEnd: "2024-06-01 07:07",
      errorNumber: "E503",
      name: "ErrorName 4",
      keycamStatus: "OFF",
      loadTon: "75 ton",
    },
  ],
  "2": [
    {
      machineName: "Machine 2",
      errorStart: "2024-06-01 01:57",
      errorEnd: "2024-06-01 02:35",
      errorNumber: "E995",
      name: "ErrorName 3",
      keycamStatus: "ON",
      loadTon: "17 ton",
    },
    {
      machineName: "Machine 2",
      errorStart: "2024-06-01 16:16",
      errorEnd: "2024-06-01 17:05",
      errorNumber: "E240",
      name: "ErrorName 4",
      keycamStatus: "ON",
      loadTon: "40 ton",
    },
  ],
  "3": [
    {
      machineName: "Machine 3",
      errorStart: "2024-06-01 06:39",
      errorEnd: "2024-06-01 08:39",
      errorNumber: "E126",
      name: "ErrorName 3",
      keycamStatus: "ON",
      loadTon: "90 ton",
    },
    {
      machineName: "Machine 3",
      errorStart: "2024-06-01 14:02",
      errorEnd: "2024-06-01 15:28",
      errorNumber: "E855",
      name: "ErrorName 4",
      keycamStatus: "ON",
      loadTon: "49 ton",
    },
  ],
  "4": [
    {
      machineName: "Machine 4",
      errorStart: "2024-06-01 18:43",
      errorEnd: "2024-06-01 19:48",
      errorNumber: "E728",
      name: "ErrorName 5",
      keycamStatus: "OFF",
      loadTon: "61 ton",
    },
    {
      machineName: "Machine 4",
      errorStart: "2024-06-01 20:12",
      errorEnd: "2024-06-01 21:09",
      errorNumber: "E563",
      name: "ErrorName 3",
      keycamStatus: "ON",
      loadTon: "99 ton",
    },
  ],
  "5": [
    {
      machineName: "Machine 5",
      errorStart: "2024-06-01 01:31",
      errorEnd: "2024-06-01 02:47",
      errorNumber: "E281",
      name: "ErrorName 3",
      keycamStatus: "OFF",
      loadTon: "90 ton",
    },
    {
      machineName: "Machine 5",
      errorStart: "2024-06-01 04:53",
      errorEnd: "2024-06-01 05:22",
      errorNumber: "E140",
      name: "ErrorName 4",
      keycamStatus: "OFF",
      loadTon: "31 ton",
    },
  ],
  "6": [
    {
      machineName: "Machine 6",
      errorStart: "2024-06-01 03:49",
      errorEnd: "2024-06-01 05:03",
      errorNumber: "E155",
      name: "ErrorName 3",
      keycamStatus: "OFF",
      loadTon: "58 ton",
    },
  ],
  "7": [
    {
      machineName: "Machine 7",
      errorStart: "2024-06-01 00:11",
      errorEnd: "2024-06-01 01:54",
      errorNumber: "E980",
      name: "ErrorName 3",
      keycamStatus: "ON",
      loadTon: "55 ton",
    },
  ],
  "8": [
    {
      machineName: "Machine 8",
      errorStart: "2024-06-01 05:24",
      errorEnd: "2024-06-01 07:24",
      errorNumber: "E376",
      name: "ErrorName 1",
      keycamStatus: "OFF",
      loadTon: "37 ton",
    },
  ],
  "9": [
    {
      machineName: "Machine 9",
      errorStart: "2024-06-01 01:40",
      errorEnd: "2024-06-01 03:19",
      errorNumber: "E907",
      name: "ErrorName 5",
      keycamStatus: "ON",
      loadTon: "84 ton",
    },
  ],
  "10": [
    {
      machineName: "Machine 10",
      errorStart: "2024-06-01 01:11",
      errorEnd: "2024-06-01 02:16",
      errorNumber: "E730",
      name: "ErrorName 1",
      keycamStatus: "ON",
      loadTon: "81 ton",
    },
  ],
  "11": [
    {
      machineName: "Machine 11",
      errorStart: "2024-06-01 10:52",
      errorEnd: "2024-06-01 11:46",
      errorNumber: "E889",
      name: "ErrorName 2",
      keycamStatus: "OFF",
      loadTon: "30 ton",
    },
  ],
  "12": [
    {
      machineName: "Machine 12",
      errorStart: "2024-06-01 01:21",
      errorEnd: "2024-06-01 01:55",
      errorNumber: "E456",
      name: "ErrorName 2",
      keycamStatus: "OFF",
      loadTon: "39 ton",
    },
  ],
  "13": [
    {
      machineName: "Machine 13",
      errorStart: "2024-06-01 09:22",
      errorEnd: "2024-06-01 10:22",
      errorNumber: "E522",
      name: "ErrorName 5",
      keycamStatus: "OFF",
      loadTon: "79 ton",
    },
  ],
  "14": [
    {
      machineName: "Machine 14",
      errorStart: "2024-06-01 00:19",
      errorEnd: "2024-06-01 01:03",
      errorNumber: "E273",
      name: "ErrorName 2",
      keycamStatus: "OFF",
      loadTon: "81 ton",
    },
  ],
  "15": [
    {
      machineName: "Machine 15",
      errorStart: "2024-06-01 19:39",
      errorEnd: "2024-06-01 21:08",
      errorNumber: "E860",
      name: "ErrorName 3",
      keycamStatus: "OFF",
      loadTon: "20 ton",
    },
  ],
  "16": [
    {
      machineName: "Machine 16",
      errorStart: "2024-06-01 20:55",
      errorEnd: "2024-06-01 22:25",
      errorNumber: "E758",
      name: "ErrorName 1",
      keycamStatus: "ON",
      loadTon: "69 ton",
    },
  ],
  "17": [
    {
      machineName: "Machine 17",
      errorStart: "2024-06-01 05:58",
      errorEnd: "2024-06-01 06:32",
      errorNumber: "E320",
      name: "ErrorName 5",
      keycamStatus: "OFF",
      loadTon: "30 ton",
    },
    {
      machineName: "Machine 17",
      errorStart: "2024-06-01 07:41",
      errorEnd: "2024-06-01 08:06",
      errorNumber: "E272",
      name: "ErrorName 3",
      keycamStatus: "OFF",
      loadTon: "11 ton",
    },
  ],
  "18": [
    {
      machineName: "Machine 18",
      errorStart: "2024-06-01 17:24",
      errorEnd: "2024-06-01 18:43",
      errorNumber: "E824",
      name: "ErrorName 1",
      keycamStatus: "ON",
      loadTon: "83 ton",
    },
    {
      machineName: "Machine 18",
      errorStart: "2024-06-01 11:41",
      errorEnd: "2024-06-01 13:22",
      errorNumber: "E873",
      name: "ErrorName 5",
      keycamStatus: "OFF",
      loadTon: "63 ton",
    },
  ],
  "19": [
    {
      machineName: "Machine 19",
      errorStart: "2024-06-01 05:58",
      errorEnd: "2024-06-01 07:46",
      errorNumber: "E615",
      name: "ErrorName 3",
      keycamStatus: "OFF",
      loadTon: "61 ton",
    },
  ],
  "20": [
    {
      machineName: "Machine 20",
      errorStart: "2024-06-01 02:13",
      errorEnd: "2024-06-01 03:10",
      errorNumber: "E142",
      name: "ErrorName 1",
      keycamStatus: "OFF",
      loadTon: "10 ton",
    },
  ],
};

const machineErrorViewMockupData: Record<
  string,
  Array<{
    errorNumber: string;
    name: string;
    totalErrorTime: string;
    averageErrorReleaseTime: string;
    occurrenceCount: string;
    errorOccurrenceCount: string;
  }>
> = {
  "1": [
    {
      errorNumber: "E503",
      name: "ErrorName 4",
      totalErrorTime: "1 hour",
      averageErrorReleaseTime: "30 minutes",
      occurrenceCount: "2",
      errorOccurrenceCount: "1",
    },
  ],
  "2": [
    {
      errorNumber: "E995",
      name: "ErrorName 3",
      totalErrorTime: "38 minutes",
      averageErrorReleaseTime: "19 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
    {
      errorNumber: "E240",
      name: "ErrorName 4",
      totalErrorTime: "49 minutes",
      averageErrorReleaseTime: "24.5 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  // Add more mockup data as needed
  "3": [
    {
      errorNumber: "E126",
      name: "ErrorName 3",
      totalErrorTime: "2 hours",
      averageErrorReleaseTime: "1 hour",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
    {
      errorNumber: "E855",
      name: "ErrorName 4",
      totalErrorTime: "1 hour 26 minutes",
      averageErrorReleaseTime: "43 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "4": [
    {
      errorNumber: "E728",
      name: "ErrorName 5",
      totalErrorTime: "1 hour 5 minutes",
      averageErrorReleaseTime: "32.5 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
    {
      errorNumber: "E563",
      name: "ErrorName 3",
      totalErrorTime: "57 minutes",
      averageErrorReleaseTime: "28.5 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "5": [
    {
      errorNumber: "E281",
      name: "ErrorName 3",
      totalErrorTime: "1 hour 16 minutes",
      averageErrorReleaseTime: "38 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
    {
      errorNumber: "E140",
      name: "ErrorName 4",
      totalErrorTime: "29 minutes",
      averageErrorReleaseTime: "14.5 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "6": [
    {
      errorNumber: "E155",
      name: "ErrorName 3",
      totalErrorTime: "1 hour 14 minutes",
      averageErrorReleaseTime: "37 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "7": [
    {
      errorNumber: "E980",
      name: "ErrorName 3",
      totalErrorTime: "1 hour 43 minutes",
      averageErrorReleaseTime: "51.5 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "8": [
    {
      errorNumber: "E376",
      name: "ErrorName 1",
      totalErrorTime: "1 hour 30 minutes",
      averageErrorReleaseTime: "45 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "9": [
    {
      errorNumber: "E907",
      name: "ErrorName 5",
      totalErrorTime: "1 hour 39 minutes",
      averageErrorReleaseTime: "49.5 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "10": [
    {
      errorNumber: "E730",
      name: "ErrorName 1",
      totalErrorTime: "1 hour 5 minutes",
      averageErrorReleaseTime: "32.5 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "11": [
    {
      errorNumber: "E889",
      name: "ErrorName 2",
      totalErrorTime: "54 minutes",
      averageErrorReleaseTime: "27 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "12": [
    {
      errorNumber: "E456",
      name: "ErrorName 2",
      totalErrorTime: "34 minutes",
      averageErrorReleaseTime: "17 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "13": [
    {
      errorNumber: "E522",
      name: "ErrorName 5",
      totalErrorTime: "1 hour",
      averageErrorReleaseTime: "30 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "14": [
    {
      errorNumber: "E273",
      name: "ErrorName 2",
      totalErrorTime: "44 minutes",
      averageErrorReleaseTime: "22 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "15": [
    {
      errorNumber: "E860",
      name: "ErrorName 3",
      totalErrorTime: "1 hour 29 minutes",
      averageErrorReleaseTime: "44.5 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "16": [
    {
      errorNumber: "E758",
      name: "ErrorName 1",
      totalErrorTime: "1 hour 30 minutes",
      averageErrorReleaseTime: "45 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "17": [
    {
      errorNumber: "E320",
      name: "ErrorName 5",
      totalErrorTime: "34 minutes",
      averageErrorReleaseTime: "17 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
    {
      errorNumber: "E272",
      name: "ErrorName 3",
      totalErrorTime: "25 minutes",
      averageErrorReleaseTime: "12.5 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "18": [
    {
      errorNumber: "E824",
      name: "ErrorName 1",
      totalErrorTime: "1 hour 19 minutes",
      averageErrorReleaseTime: "39.5 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
    {
      errorNumber: "E873",
      name: "ErrorName 5",
      totalErrorTime: "1 hour 41 minutes",
      averageErrorReleaseTime: "50.5 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "19": [
    {
      errorNumber: "E615",
      name: "ErrorName 3",
      totalErrorTime: "1 hour 48 minutes",
      averageErrorReleaseTime: "54 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
  "20": [
    {
      errorNumber: "E142",
      name: "ErrorName 1",
      totalErrorTime: "43 minutes",
      averageErrorReleaseTime: "21.5 minutes",
      occurrenceCount: "1",
      errorOccurrenceCount: "1",
    },
  ],
};
